import { numeroPedido, type IdentificacaoPedido } from "../../lib/pedidosManuais";
import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { mensagemDeErro, novaChave, rpc } from "../../lib/rpc";
import { sinalConcluido, sinalErro, sinalOk } from "../../lib/sinais";
import { fmtNum } from "../../lib/formato";
import type { DetalheMontagem } from "../../lib/tipos";
import { LeituraCodigo } from "../../componentes/LeituraCodigo";
import { AlertaErro, Confirmar } from "../../componentes/Alerta";
import { Botao, Selo, Vazio } from "../../componentes/ui";

interface PedidoFila extends IdentificacaoPedido {
  pedido_id: string; numero: number | null; numero_ecommerce: string | null; canal: string | null; tags: string[];
  forma_envio: string | null; onda: number; carrinho: string | null; itens: string | null;
  em_montagem_por: string | null; minha: boolean;
}
type Fila = { ok: boolean; minha_montagem: string | null; pedidos: PedidoFila[] };

/**
 * Separação na esteira (separador ou líder): lista os pedidos já pré-separados de
 * todas as ondas. Abre um pedido, bipa a caixa preta e depois os itens; no último
 * item o pedido fecha sozinho na caixa (no banco e no código: "montagem").
 */
export default function Montagem() {
  const cliente = useQueryClient();
  const [escolhido, setEscolhido] = useState<PedidoFila | null>(null);
  const [montagem, setMontagem] = useState<DetalheMontagem | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [detalheErro, setDetalheErro] = useState<DetalheMontagem | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [lampejo, setLampejo] = useState(0);
  const [tremor, setTremor] = useState(0);
  const [ocupado, setOcupado] = useState(false);
  const [confirmarFalta, setConfirmarFalta] = useState(false);

  const fila = useQuery({
    queryKey: ["montagem-fila"],
    queryFn: () => rpc<Fila>("wms_montagem_fila"),
    refetchInterval: 10_000,
  });
  const recarregar = useCallback(() => void cliente.invalidateQueries({ queryKey: ["montagem-fila"] }), [cliente]);

  // retoma o pedido que esta pessoa já estava montando
  useEffect(() => {
    if (montagem || escolhido || !fila.data?.minha_montagem) return;
    const minha = fila.data.pedidos.find((p) => p.minha);
    if (minha) setEscolhido(minha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fila.data]);
  useEffect(() => {
    if (!escolhido?.minha || montagem) return;
    void rpc<DetalheMontagem>("wms_montagem_abrir", { p_pedido_id: escolhido.pedido_id, p_codigo_contentor: "" }).then((r) => {
      if (r.ok) setMontagem(r);
    });
  }, [escolhido, montagem]);

  function falhou(msg: string, detalhe?: DetalheMontagem) {
    sinalErro();
    setTremor((n) => n + 1);
    setDetalheErro(detalhe ?? null);
    setErro(msg);
  }

  function voltarLista(mensagem?: string) {
    setMontagem(null);
    setEscolhido(null);
    setOk(mensagem ?? null);
    recarregar();
  }

  async function bipar(codigo: string) {
    if (!escolhido || ocupado) return;
    setOcupado(true);
    try {
      if (!montagem) {
        const r = await rpc<DetalheMontagem>("wms_montagem_abrir", { p_pedido_id: escolhido.pedido_id, p_codigo_contentor: codigo, p_idem: novaChave() });
        if (r.ok) {
          sinalOk();
          setLampejo((n) => n + 1);
          setMontagem(r);
        } else if (r.codigo_erro === "CARRINHO_INSUFICIENTE") falhou(r.mensagem ?? "Carrinho sem os itens do pedido", r);
        else falhou(r.mensagem ?? "Caixa preta recusada");
        return;
      }
      const r = await rpc<DetalheMontagem>("wms_montagem_bipar", { p_montagem_id: montagem.montagem.id, p_codigo: codigo, p_idem: novaChave() });
      if (r.montagem) setMontagem(r);
      if (!r.ok) return falhou(r.mensagem ?? "Leitura recusada");
      if (r.montagem?.status === "concluida") {
        sinalConcluido();
        const sobras = r.onda_concluida && r.onda?.carrinho_itens.length
          ? ` Onda ${r.onda.onda.numero} terminou e sobrou item no carrinho ${r.onda.onda.carrinho}: devolva às posições.` : "";
        voltarLista(`Pedido ${r.montagem.pedido.numero ?? ""} fechado na caixa ${r.montagem.contentor ?? ""}: coloque a caixa na esteira.${sobras}`);
        return;
      }
      sinalOk();
      setLampejo((n) => n + 1);
    } catch (e) {
      falhou(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  async function sair() {
    if (montagem) {
      setOcupado(true);
      try {
        await rpc("wms_montagem_cancelar", { p_montagem_id: montagem.montagem.id, p_falta: false });
      } catch (e) {
        setErro(mensagemDeErro(e));
      } finally {
        setOcupado(false);
      }
    }
    voltarLista();
  }

  async function marcarFalta() {
    if (!escolhido) return;
    setOcupado(true);
    try {
      const r = await rpc("wms_montagem_marcar_falta", { p_pedido_id: escolhido.pedido_id });
      setConfirmarFalta(false);
      setErro(null);
      setDetalheErro(null);
      if (r.ok) voltarLista(`Pedido ${escolhido.numero ?? ""} marcado como falta: volta para a fila quando o estoque aparecer.`);
      else setErro(r.mensagem ?? "Não foi possível marcar a falta");
    } catch (e) {
      falhou(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  const faltandoDetalhe = detalheErro?.faltando?.length ? (
    <div className="flex flex-col gap-3">
      <ul className="list-disc pl-6">
        {detalheErro.faltando.map((f) => <li key={f.sku}>{f.nome}: precisa {fmtNum(f.precisa)}, tem {fmtNum(Math.max(0, f.disponivel))}</li>)}
      </ul>
      <button type="button" onClick={() => void marcarFalta()} className="min-h-12 rounded-md border-2 border-white px-4 font-bold">
        Marcar pedido como falta
      </button>
    </div>
  ) : undefined;

  // ---------------------------------------------------------------------------
  // Pedido aberto: caixa preta primeiro, depois os itens
  // ---------------------------------------------------------------------------
  if (escolhido) {
    return (
      <div className="flex flex-col gap-3 pb-4">
        <AlertaErro mensagem={erro} detalhe={faltandoDetalhe} onFechar={() => { setErro(null); setDetalheErro(null); }} />
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-tinta px-3 py-2 text-white">
          <span className="font-sinal text-2xl font-bold">Pedido {numeroPedido(escolhido)}</span>
          {escolhido.tags.map((t) => <Selo key={t} tom="faixa">{t}</Selo>)}
          <span className="opacity-80">onda {escolhido.onda}</span>
          {escolhido.carrinho && <span className="ml-auto rounded bg-white/15 px-2 py-0.5 font-sinal text-lg">carrinho {escolhido.carrinho}</span>}
        </div>

        {montagem ? (
          <>
            <p className="rounded-md bg-ok-claro px-3 py-2 text-lg font-semibold text-ok">Caixa preta {montagem.montagem.contentor}</p>
            <section className="rounded-lg border-2 border-tinta bg-chapa">
              <ul className="divide-y divide-aco">
                {montagem.itens.map((i) => {
                  const feito = i.bipada >= i.esperada;
                  return (
                    <li key={i.produto_id} className={`flex items-center justify-between gap-3 px-4 py-3 ${feito ? "bg-ok-claro" : ""}`}>
                      <div>
                        <p className="text-xl font-semibold leading-tight">{i.nome}</p>
                        <p className="text-aco-escuro">{i.sku}</p>
                      </div>
                      <span className={`font-sinal text-4xl font-bold ${feito ? "text-ok" : ""}`}>{fmtNum(i.bipada)}/{fmtNum(i.esperada)}</span>
                    </li>
                  );
                })}
              </ul>
            </section>
            <LeituraCodigo rotulo="Bipe os itens do pedido" onLeitura={(c) => void bipar(c)} desabilitado={ocupado} lampejo={lampejo} erro={tremor} />
            <p className="text-sm text-aco-escuro">No último item o pedido fecha sozinho na caixa preta.</p>
          </>
        ) : (
          <>
            <p className="text-lg">Pegue uma caixa preta vazia e bipe a etiqueta dela.</p>
            <p className="text-aco-escuro">Itens: {escolhido.itens}</p>
            <LeituraCodigo rotulo="Bipe a caixa preta" onLeitura={(c) => void bipar(c)} desabilitado={ocupado} lampejo={lampejo} erro={tremor} />
          </>
        )}

        <div className="flex flex-wrap gap-2">
          <Botao variante="secundario" onClick={() => void sair()} disabled={ocupado}>Voltar para a lista</Botao>
          <Botao variante="perigo" onClick={() => setConfirmarFalta(true)} disabled={ocupado}>Faltou item no carrinho</Botao>
        </div>
        <Confirmar aberto={confirmarFalta} titulo={`Faltou item no pedido ${numeroPedido(escolhido)}?`} rotuloConfirmar="Marcar falta" perigo ocupado={ocupado}
          onConfirmar={() => void marcarFalta()} onCancelar={() => setConfirmarFalta(false)}>
          O pedido sai da separação e volta para a fila como sem saldo quando a onda terminar. Os itens já bipados continuam no carrinho.
        </Confirmar>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Lista de pedidos pré-separados
  // ---------------------------------------------------------------------------
  const pedidos = fila.data?.pedidos ?? [];
  return (
    <div className="flex flex-col gap-3 pb-4">
      <AlertaErro mensagem={erro} onFechar={() => setErro(null)} />
      <h1 className="font-sinal text-3xl font-bold">Separação</h1>
      {ok && <p className="rounded-lg bg-ok px-4 py-3 text-lg font-bold text-white" role="status">{ok}</p>}
      <p className="text-aco-escuro">Pedidos já pré-separados no carrinho. Escolha um, bipe a caixa preta e depois os itens.</p>
      {pedidos.length ? (
        <ul className="flex flex-col gap-2">
          {pedidos.map((p) => {
            const ocupadoPorOutro = !!p.em_montagem_por && !p.minha;
            return (
              <li key={p.pedido_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border-2 border-aco bg-chapa px-3 py-3">
                <div>
                  <p className="font-sinal text-2xl font-bold">Pedido {numeroPedido(p)} {p.tags.map((t) => <Selo key={t} tom="faixa">{t}</Selo>)}</p>
                  <p className="text-sm">{p.itens}</p>
                  <p className="text-xs text-aco-escuro">onda {p.onda} · carrinho {p.carrinho} {p.forma_envio && `· ${p.forma_envio}`}</p>
                </div>
                {ocupadoPorOutro
                  ? <Selo tom="alerta">{`separando: ${p.em_montagem_por}`}</Selo>
                  : <Botao onClick={() => { setOk(null); setEscolhido(p); }} className="min-h-12">{p.minha ? "Continuar" : "Montar"}</Botao>}
              </li>
            );
          })}
        </ul>
      ) : <Vazio>{fila.isLoading ? "Carregando..." : fila.error ? mensagemDeErro(fila.error) : "Nenhum pedido pré-separado esperando a separação."}</Vazio>}
    </div>
  );
}
